
import React, { useState, useEffect } from "react";
import axiosInstance from '../axiosConfig';
import { Link } from 'react-router-dom';
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import '../styles.css';

const LeaveManagement = () => {
  const [userData, setUserData] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAllBalances, setShowAllBalances] = useState(false);
  const [showAccrualModal, setShowAccrualModal] = useState(false);
  const [accrualLoading, setAccrualLoading] = useState(false);
  const [accrualResult, setAccrualResult] = useState(null);
  const [accrualFormData, setAccrualFormData] = useState({
    dry_run: true,
    branch_id: '',
    user_id: '',
    force: false
  });
  const currentUserId = localStorage.getItem('id'); 
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    if (screenWidth < 520) {
      setIsSidebarVisible(false);
    } else {
      setIsSidebarVisible(true);
    }
  }, []);

  const handleToggleClick = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => setUserData(response.data))
        .catch((error) => console.error('Error fetching user data:', error));
    }
  }, [currentUserId]);

  // Fetch all users for admin balances table
  useEffect(() => {
    if (['admin', 'manager'].includes(userData?.role?.name)) {
      setLoadingUsers(true);
      axiosInstance.get('/user/')
        .then(response => {
          setAllUsers(response.data.filter(u => u.is_active));
          setLoadingUsers(false);
        })
        .catch(error => {
          console.error('Error fetching users:', error);
          setLoadingUsers(false);
        });
    }
  }, [userData]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const handleAccrualSubmit = async () => {
    setAccrualLoading(true);
    setAccrualResult(null);
    
    try {
      const payload = {
        dry_run: accrualFormData.dry_run
      };
      
      if (accrualFormData.branch_id) {
        payload.branch_id = parseInt(accrualFormData.branch_id);
      }
      if (accrualFormData.user_id) {
        payload.user_id = parseInt(accrualFormData.user_id);
      }
      if (accrualFormData.force) {
        payload.force = accrualFormData.force;
      }
      
      const response = await axiosInstance.post('/accrue-leaves/', payload);
      setAccrualResult(response.data);
      
      // If not dry run and successful, refresh users
      if (!accrualFormData.dry_run && response.data.status === 'success') {
        const updatedUsers = await axiosInstance.get('/user/');
        setAllUsers(updatedUsers.data.filter(u => u.is_active));
      }
      
    } catch (error) {
      console.error('Error accruing leaves:', error);
      setAccrualResult({ 
        error: error.response?.data?.error || 'Failed to accrue leaves',
        status: 'error'
      });
    } finally {
      setAccrualLoading(false);
    }
  };

  const resetAccrualForm = () => {
    setAccrualFormData({
      dry_run: true,
      branch_id: '',
      user_id: '',
      force: false
    });
    setAccrualResult(null);
  };

  const menuItems = [
    { icon: '🏠', label: 'Dashboard', path: '/user-dashboard' },
    { icon: '👤', label: 'My Profile', path: `/user-profile/${userData?.id}` },
    { icon: '📅', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
    { icon: '📋', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
    { icon: '✏️', label: 'Create Leave Request', path: `/leave-request/${userData?.id}` },
    { icon: '💰', label: 'Leave Balance', path: '/leave-balance' },
    { icon: '📊', label: 'Leave Transactions', path: '/leave-transaction/' },
    // { icon: '📅', label: 'Calendar', path: '/calendar' },
    // { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
    { icon: '🆔', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
  ];

  const profilePhotoUrl = userData?.profile_photo?.startsWith('http://') 
    ? userData?.profile_photo.replace('http://', 'https://') 
    : userData?.profile_photo;

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header 
        onToggleClick={handleToggleClick} 
        isSidebarVisible={isSidebarVisible} 
      />
      
      <Sidebar 
        isSidebarVisible={isSidebarVisible}
        currentUser={userData}
        menuItems={menuItems}
        handleLogout={handleLogout}
      />
      
      <div className="dashboard-main-content">
        <div className="leave-management-container">
          <div className="leave-management-header">
            <h1>Leave Management</h1>
            <p>Manage your leave requests, check balance, and view transactions</p>
          </div>

          <div className="leave-cards-grid">
            <Link to={`/leave-request/${userData?.id}`} className="leave-card">
              <div className="leave-card-icon">
                <img src="/create-leave.png" alt="Create Leave Request" />
              </div>
              <div className="leave-card-content">
                <h3>Create Leave Request</h3>
                <p>Submit a new leave request for approval</p>
              </div>
              <span className="leave-card-arrow">→</span>
            </Link>

            <Link to="/leave-balance" className="leave-card">
              <div className="leave-card-icon">
                <img src="/leave.jpg" alt="Leave Balance" />
              </div>
              <div className="leave-card-content">
                <h3>My Leave Balance</h3>
                <p>View your available leave balance</p>
              </div>
              <span className="leave-card-arrow">→</span>
            </Link>

            <Link to="/leave-transaction/" className="leave-card">
              <div className="leave-card-icon">
                <img src="/apply-leave.jpg" alt="Leave Transactions" />
              </div>
              <div className="leave-card-content">
                <h3>My Leave Transactions</h3>
                <p>View your leave history and status</p>
              </div>
              <span className="leave-card-arrow">→</span>
            </Link>

            {/* Admin/Manager Accrual Quick Action */}
            {['admin', 'manager'].includes(userData?.role?.name) && (
              <>
                <button 
                  className="leave-card" 
                  style={{cursor: 'pointer', border: '2px solid #f47522'}}
                  onClick={() => {
                    resetAccrualForm();
                    setShowAccrualModal(true);
                  }}
                >
                  <div className="leave-card-icon" style={{background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                    <span style={{fontSize: '24px'}}>📈</span>
                  </div>
                  <div className="leave-card-content">
                    <h3>Monthly Leave Accrual</h3>
                    <p>Accrue monthly leave balances for employees</p>
                  </div>
                  <span className="leave-card-arrow">→</span>
                </button>

                <button 
                  className="leave-card" 
                  style={{cursor: 'pointer', border: '2px solid #f47522'}}
                  onClick={() => setShowAllBalances(!showAllBalances)}
                >
                  <div className="leave-card-icon" style={{background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'}}>
                    <span style={{fontSize: '24px'}}>👥</span>
                  </div>
                  <div className="leave-card-content">
                    <h3>All Users Balances</h3>
                    <p style={{color: showAllBalances ? '#10b981' : '#64748b'}}>
                      {showAllBalances ? 'Hide' : 'View'} all employees leave balances
                    </p>
                  </div>
                  <span className="leave-card-arrow">→</span>
                </button>
              </>
            )}
          </div>

          {/* All Users Leave Balances Table - Admin/Manager */}
          {['admin', 'manager'].includes(userData?.role?.name) && showAllBalances && (
            <div className="dashboard-card" style={{marginTop: '24px'}}>
              <div className="card-header">
                <span className="card-icon">📊</span>
                <h3>All Active Users Leave Balances</h3>
              </div>
              <div className="card-body">
                {loadingUsers ? (
                  <div style={{textAlign: 'center', padding: '40px'}}>
                    <div className="loading-spinner" style={{margin: '0 auto 16px', width: '48px', height: '48px'}}></div>
                    <p>Loading users...</p>
                  </div>
                ) : allUsers.length > 0 ? (
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Employee Code</th>
                          <th>Name</th>
                          <th>EL Balance</th>
                          <th>SL Balance</th>
                          <th>Total Days</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allUsers.map((user) => {
                          const balances = user.leave_balances || {};
                          const el = parseFloat(balances.EL || 0);
                          const sl = parseFloat(balances.SL || 0);
                          const total = el + sl;
                          return (
                            <tr key={user.id}>
                              <td><strong>{user.employee_code}</strong></td>
                              <td>{user.first_name} {user.last_name}</td>
                              <td style={{fontWeight: '600', color: el > 0 ? '#10b981' : '#ef4444'}}>
                                {el.toFixed(2)}
                              </td>
                              <td style={{fontWeight: '600', color: sl > 0 ? '#10b981' : '#ef4444'}}>
                                {sl.toFixed(2)}
                              </td>
                              <td style={{fontWeight: '700', color: total > 0 ? '#3b82f6' : '#6b7280'}}>
                                {total.toFixed(2)}
                              </td>
                              <td>
                                <Link 
                                  to={`/user-profile/${user.id}`} 
                                  style={{color: '#f47522', fontWeight: '500'}}
                                >
                                  View
                                </Link>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{textAlign: 'center', padding: '40px', color: '#64748b'}}>
                    No active users found
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="leave-quick-info">
            <div className="quick-info-card">
              <div className="quick-info-header">
                <span className="info-icon">ℹ️</span>
                <h3>Quick Information</h3>
              </div>
              <div className="quick-info-content">
                <p>Use the links above to:</p>
                <ul>
                  <li>Create a new leave request</li>
                  <li>Check your leave balance</li>
                  <li>View your leave transaction history</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Leave Accrual Modal */}
      {showAccrualModal && (
        <div className="modal-overlay" onClick={() => setShowAccrualModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Monthly Leave Accrual</h3>
              <button className="modal-close" onClick={() => setShowAccrualModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="accrual-form-section">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={accrualFormData.dry_run}
                      onChange={(e) => setAccrualFormData({...accrualFormData, dry_run: e.target.checked})}
                    />
                    <span>Dry Run (Preview Only)</span>
                  </label>
                  <small>Check to see what would happen without actually updating balances</small>
                </div>

                <div className="form-group">
                  <label>Branch ID (Optional)</label>
                  <input
                    type="number"
                    value={accrualFormData.branch_id}
                    onChange={(e) => setAccrualFormData({...accrualFormData, branch_id: e.target.value})}
                    placeholder="Leave empty for all branches"
                  />
                </div>

                <div className="form-group">
                  <label>User ID (Optional)</label>
                  <input
                    type="number"
                    value={accrualFormData.user_id}
                    onChange={(e) => setAccrualFormData({...accrualFormData, user_id: e.target.value})}
                    placeholder="Leave empty for all users"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={accrualFormData.force}
                      onChange={(e) => setAccrualFormData({...accrualFormData, force: e.target.checked})}
                    />
                    <span>Force Recalculation</span>
                  </label>
                  <small>Recalculate from user's start date instead of last accrual date</small>
                </div>
              </div>

              {accrualResult && (
                <div className={`accrual-result ${accrualResult.error ? 'error' : 'success'}`}>
                  {accrualResult.error ? (
                    <p>❌ {accrualResult.error}</p>
                  ) : (
                    <>
                      <div className="result-summary">
                        <h4>Accrual Summary</h4>
                        <div className="summary-stats">
                          <div className="stat-item">
                            <span className="stat-label">Total Users:</span>
                            <span className="stat-value">{accrualResult.summary?.total_users || 0}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Updated:</span>
                            <span className="stat-value success">{accrualResult.summary?.updated_count || 0}</span>
                          </div>
                          <div className="stat-item">
                            <span className="stat-label">Skipped:</span>
                            <span className="stat-value warning">{accrualResult.summary?.skipped_count || 0}</span>
                          </div>
                        </div>
                      </div>
                      
                      {accrualResult.updated_users && accrualResult.updated_users.length > 0 && (
                        <div className="updated-users-list">
                          <h5>Updated Users ({accrualResult.updated_users.length})</h5>
                          <div className="users-scroll">
                            {accrualResult.updated_users.map((user, idx) => (
                              <div key={idx} className="updated-user-item">
                                <div className="user-info">
                                  <strong>{user.employee_code}</strong>
                                  <span>{user.name}</span>
                                </div>
                                <div className="user-balances">
                                  {Object.entries(user.new_balances).map(([type, balance]) => (
                                    <span key={type} className="balance-badge">
                                      {type}: {parseFloat(balance).toFixed(2)}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {accrualResult.skipped_users && accrualResult.skipped_users.length > 0 && (
                        <div className="skipped-users-list">
                          <h5>Skipped Users ({accrualResult.skipped_users.length})</h5>
                          <div className="users-scroll">
                            {accrualResult.skipped_users.map((user, idx) => (
                              <div key={idx} className="skipped-user-item">
                                <span>{user.employee_code}</span>
                                <span className="skip-reason">{user.reason}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => {
                  resetAccrualForm();
                  setShowAccrualModal(false);
                }}
                disabled={accrualLoading}
              >
                Close
              </button>
              <button 
                className="btn-primary" 
                onClick={handleAccrualSubmit}
                disabled={accrualLoading}
              >
                {accrualLoading ? 'Processing...' : accrualFormData.dry_run ? 'Preview Accrual' : 'Apply Accrual'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* ID Card Popup */}
      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>×</button>
            
            <div className="id-card-header">
              <img src="/Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            
            <div className="id-card-body">
              <div className="Id-card-profile">
                <img
                  src={profilePhotoUrl || '/profile.jpg'}
                  alt="Profile"
                  className="id-card-photo"
                />
                <div className="id-card-info">
                  <h4>{userData?.first_name} {userData?.last_name}</h4>
                  <p className="id-designation">{userData?.designation}</p>
                  <div className="id-dob">
                    <strong>DOB:</strong>
                    <span>
                      {userData?.date_of_birth
                        ? new Date(userData.date_of_birth).toLocaleDateString("en-GB").replace(/\//g, "-")
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
              
              <hr className="id-card-divider" />
              
              <div className="Id-card-details">
                {[
                  { label: "Emp ID", value: userData?.employee_code },
                  { label: "Department", value: userData?.department },
                  { label: "ESIC No.", value: userData?.esic_no },
                  { label: "UAN No.", value: userData?.uan_no },
                  { label: "Gender", value: userData?.gender },
                  { label: "Contact No.", value: userData?.contact_number },
                ].map((item, idx) => (
                  <div key={idx} className="detail-row">
                    <strong>{item.label}:</strong>
                    <span>{item.value || "N/A"}</span>
                  </div>
                ))}
              </div>
              
              {typeof userData?.work_location_name === "string" &&
                userData.work_location_name.toLowerCase() !== "noida" && (
                <div className="Id-card-duty">
                  <strong>On Contractual Duty Of:</strong>
                  <span>{userData?.duty_of || "N/A"}</span>
                </div>
              )}
              
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveManagement;


